// Account endpoints (v1 `/me`). Sign-in and sign-up live in ../auth/legacy.js,
// ../auth/signup.js and ../auth/social.js (Google/Apple/Facebook SDKs); password recovery goes
// through Authentik (../auth/oidc.js).
import api, {items, withIdempotencyKey} from './client';

/** v1 profile -> the shape existing pages consume. */
export const toAccount = (p) => ({
  ...p,
  avatar_url: p.profile_picture,
  doctor: p.doctor_id,
});

export const getAccountData = () => api.get('/me').then((r) => toAccount(r.data));

export const updateAccountData = (accountData) => {
  const body = {};
  if (accountData.username !== undefined) body.username = accountData.username;
  if (accountData.phone !== undefined && accountData.phone !== '') body.phone = accountData.phone;
  if (accountData.first_name !== undefined) body.first_name = accountData.first_name;
  if (accountData.last_name !== undefined) body.last_name = accountData.last_name;
  return api.patch('/me', body).then((r) => toAccount(r.data));
};

/** Linked Google / Apple / Facebook sign-in accounts: `[{provider, email|null}]`. */
export const listSocialAccounts = () => api.get('/me/social-accounts').then((r) => items(r.data));

/** Links a provider account; `token` is the same provider SDK token sign-in uses. */
export const linkSocialAccount = (provider, token, opts = {}) =>
  api
    .post('/me/social-accounts', {provider, token}, withIdempotencyKey(opts.idempotencyKey))
    .then((r) => r.data);

/** Unlinks a provider account (409 when it is the account's last way to sign in). */
export const unlinkSocialAccount = (provider) => api.delete(`/me/social-accounts/${provider}`);

/** Deactivates the account (history is kept). */
export const deleteAccount = () => api.delete('/me');

export const getStorageUsage = () => api.get('/me/storage-usage').then((r) => r.data);

export const getHomeSummary = () => api.get('/me/home-summary').then((r) => r.data);

export const getFamilyMembers = () =>
  api.get('/me/family').then((r) => items(r.data).map((m) => ({...m, related_username: m.related_name})));

export const addFamilyMember = (email, relationship) =>
  api.post('/me/family', {email, relationship}).then((r) => r.data);

export const removeFamilyMember = (id) => api.delete(`/me/family/${id}`);

export const getDentalMessages = () => api.get('/me/messages').then((r) => r.data);

export const getPoints = () => api.get('/me/points').then((r) => r.data);

/** Starts a membership checkout; paid plans return a Stripe PaymentIntent. */
export const choosePlan = (_memberId, plan, opts = {}) =>
  api.post('/membership/checkout', {plan}, withIdempotencyKey(opts.idempotencyKey)).then((r) => ({
    ...r.data,
    id: r.data.membership_payment_id,
  }));

export const confirmMembership = (membershipPaymentId, opts = {}) =>
  api
    .post(`/membership/${membershipPaymentId}/confirm`, undefined, withIdempotencyKey(opts.idempotencyKey))
    .then((r) => r.data);
